import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import {
  type ButtonProps,
  IconButton,
  type IconButtonProps,
  Tooltip,
  keyframes,
  mergeSlotProps,
} from '@mui/material';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {type ElementType, type FC, useEffect, useState} from 'react';
import {Location, NavLink} from 'react-router';
import {useGetStacCollection} from '@src/api/catalog';
import {PROMOTE_AUTOMATIC_ZOOM_TO_COLLECTION_LOCAL_STORAGE_KEY} from '@src/constants/analyze';
import {AppLocationState} from '@src/types/appLocation';

const pulseAnimation = keyframes`
  1% {
    transform: translate(-50%, -50%) scale(1);
    opacity: .3;
  }
  80% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
`;

type ZoomMapToExtentButtonProps<
  T extends ButtonProps | IconButtonProps = IconButtonProps,
> = {
  collectionId: string;
  rootProps?: T & {
    state?: AppLocationState;
    LinkComponent?: FC<unknown>;
    to?: Location;
  };
  component?: ElementType;
  title?: string;
  enablePulse?: boolean;
};

export const ZoomMapToExtentButton: FC<ZoomMapToExtentButtonProps> = ({
  rootProps = {},
  title,
  collectionId,
  enablePulse = true,
  component: ButtonComponent = IconButton,
}) => {
  const promoteDuration = 950;
  const promoteRepeat = 3;
  const search = useOptimisticSearchParams()?.toString();
  const storageItemValue = localStorage.getItem(
    PROMOTE_AUTOMATIC_ZOOM_TO_COLLECTION_LOCAL_STORAGE_KEY,
  );
  const [showTooltip, setShowTooltip] = useState<boolean>(
    storageItemValue !== 'false',
  );

  const {data, isLoading} = useGetStacCollection(collectionId);
  const collectionExtent = data?.extent?.spatial?.bbox[0];

  useEffect(() => {
    let timeoutId = null;
    if (showTooltip) {
      timeoutId = setTimeout(() => {
        setShowTooltip(false);
        localStorage.setItem(
          PROMOTE_AUTOMATIC_ZOOM_TO_COLLECTION_LOCAL_STORAGE_KEY,
          'false',
        );
      }, promoteDuration * promoteRepeat);
    }
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [showTooltip]);

  if (isLoading || !collectionExtent) {
    return null;
  }

  return (
    <Tooltip
      open={storageItemValue === null ? showTooltip : undefined}
      title={title ?? 'Zoom to collection extent AOI'}
      placement="right"
      arrow
    >
      <ButtonComponent
        component={NavLink}
        disabled={isLoading}
        to={{search}}
        state={{mapExtent: {bbox: collectionExtent}} satisfies AppLocationState}
        role="button"
        size="small"
        color="secondary"
        {...mergeSlotProps(rootProps, {
          sx: {
            position: 'relative',
            height: 28,
            width: 28,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: 'secondary.main',
              opacity: 0,
              ...(enablePulse
                ? {
                    animation: `${pulseAnimation} ${promoteDuration}ms ease-out`,
                    animationIterationCount: promoteRepeat,
                  }
                : {}),
              pointerEvents: 'none',
              zIndex: 0,
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          },
          variant: 'outlined',
        })}
      >
        <CenterFocusStrongIcon fontSize="small" />
      </ButtonComponent>
    </Tooltip>
  );
};

export default ZoomMapToExtentButton;
